import {
  deriveLedger, fuelLogsForCar, estimateValuation, retainedFraction,
  type CanonicalStore, type Car,
} from "../../core/index.ts";
import { SplitFlapNumber } from "../components/SplitFlapNumber.tsx";
import { BackRow } from "../forms/Modal.tsx";
import { round0 } from "../format.ts";

function useEstimate(store: CanonicalStore, car: Car) {
  const logs = fuelLogsForCar(store.fuelLogs, car.id);
  const total = deriveLedger(store, car.id).total;
  return estimateValuation(car, logs, total);
}

/** Compact Garage card (mobile ValuationCard). */
export function ValuationCard({ store, car, currency, isPro, onOpen }: {
  store: CanonicalStore; car: Car; currency: string; isPro: boolean; onOpen: () => void;
}) {
  const est = useEstimate(store, car);
  return (
    <button className="panel" style={{ display: "flex", alignItems: "center", gap: 14, width: "100%", textAlign: "left", cursor: "pointer", marginTop: 14 }} onClick={onOpen}>
      <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(255,178,0,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <span className="amber" style={{ fontSize: 18 }}>↗</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="mono amber" style={{ fontSize: 10, letterSpacing: 2.2 }}>CURRENT VALUATION{isPro ? "" : " (PRO)"}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <SplitFlapNumber value={est.purchasePrice != null ? `${round0(est.currentValue)} ${currency}` : "—"} size={22} weight={500} />
          {est.depreciationPct != null && <span className="mono" style={{ fontSize: 11, color: "var(--redline)" }}>−{Math.round(est.depreciationPct)}%</span>}
        </div>
        <div className="mono muted" style={{ fontSize: 11 }}>{est.subtitle}</div>
      </div>
      <span className="mono muted" style={{ fontSize: 16 }}>›</span>
    </button>
  );
}

function Tile({ k, v }: { k: string; v: string }) {
  return (
    <div className="panel" style={{ flex: 1 }}>
      <div className="mono muted" style={{ fontSize: 9, letterSpacing: 2.2 }}>{k}</div>
      <SplitFlapNumber value={v} size={18} weight={500} />
    </div>
  );
}

/** Full valuation screen (mobile CarValuationScreen). */
export function ValuationView({ store, car, currency, metric, isPro, requirePro, onBack }: {
  store: CanonicalStore; car: Car; currency: string; metric: boolean; isPro: boolean; requirePro: () => void; onBack: () => void;
}) {
  const est = useEstimate(store, car);
  const z = !isPro;
  const money = (v: number) => `${round0(v)} ${currency}`;
  const ageStr = est.ageYears >= 1 ? `${est.ageYears.toFixed(1)} yr` : est.ageYears > 0 ? `${Math.round(est.ageYears * 12)} mo` : "—";
  const odoStr = est.odometer != null ? `${round0(metric ? est.odometer : est.odometer * 0.621371)} ${metric ? "km" : "mi"}` : "—";
  const retained = retainedFraction(est);

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="eyebrow amber" style={{ marginTop: 4 }}>APPRAISAL · HEURISTIC</div>
      <div className="headline">RESALE <span className="accent">estimate.</span></div>
      <div className="mono muted" style={{ fontSize: 10, letterSpacing: 2.2, marginTop: 4 }}>
        {car.year} · {(car.make ?? "").toUpperCase()} {(car.model ?? "").toUpperCase()}
      </div>

      <div className="panel" style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="mono muted" style={{ fontSize: 10, letterSpacing: 2.2 }}>EST. SALE VALUE TODAY</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <SplitFlapNumber value={!z && est.purchasePrice != null ? money(est.currentValue) : "—"} size={48} />
          {!z && est.depreciationPct != null && <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: "var(--redline)" }}>−{Math.round(est.depreciationPct)}%</span>}
        </div>
        {!z && est.totalLoss != null && <div className="mono muted" style={{ fontSize: 12 }}>Lost value so far · {money(est.totalLoss)}</div>}
      </div>

      {est.purchasePrice == null && (
        <div className="panel" style={{ marginTop: 20, borderColor: "rgba(255,178,0,0.35)" }}>
          <span className="mono" style={{ fontSize: 12, color: "var(--creamDim)" }}>Add the car's purchase price in Edit Car to see a real sale estimate.</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <Tile k="PURCHASE PRICE" v={!z && est.purchasePrice != null ? money(est.purchasePrice) : "—"} />
        <Tile k="AGE" v={z ? "—" : ageStr} />
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
        <Tile k="ODOMETER" v={z ? "—" : odoStr} />
        <Tile k="TOTAL SPENT" v={z ? "—" : money(est.totalSpent)} />
      </div>

      {!z && est.depreciationPct != null && (
        <div className="panel" style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span className="mono muted" style={{ fontSize: 9, letterSpacing: 2 }}>RETAINED VALUE</span>
            <span style={{ flex: 1 }} />
            <span className="mono amber" style={{ fontSize: 11, fontWeight: 700 }}>{Math.round(retained * 100)}%</span>
          </div>
          <div style={{ width: "100%", height: 10, background: "var(--edge)", borderRadius: 5, overflow: "hidden" }}>
            <div style={{ width: `${retained * 100}%`, height: 10, background: "linear-gradient(90deg, var(--amber), var(--amberHot))", borderRadius: 5 }} />
          </div>
        </div>
      )}

      <div className="panel" style={{ marginTop: 20, display: "flex", flexDirection: "column", gap: 8 }}>
        <span className="mono amber" style={{ fontSize: 9, letterSpacing: 2 }}>HOW IT'S CALCULATED</span>
        <span className="mono muted" style={{ fontSize: 12, lineHeight: 1.6 }}>
          Heuristic depreciation: ~20% first year, ~12%/yr years 2–5, ~8%/yr after. Adjusted down for high odometer vs a 15,000 km/yr baseline. Not an official appraisal — use it as a ballpark.
        </span>
      </div>

      {z && (
        <button className="btn primary" style={{ marginTop: 20, width: "100%" }} onClick={requirePro}>UNLOCK PRO</button>
      )}
      <div style={{ height: 30 }} />
    </div>
  );
}
